// Admin: edit the guided tutorial. Text, pictures and videos, step order, which software each part is for.
// Saving writes site/tutorial.json; new studios see it straight away.
import { adminPutSite, type Studio } from '../data';
import { html, raw, icon, shell, toast, confirmDialog, $, $$ } from '../ui';
import { loadTutorial, forgetTutorial, DEFAULT_TUTORIAL, type TutorialData, type TutorialMedia, type TutorialChapter } from '../tutorial/content';
import { openTutorial, mediaHtml } from '../tutorial/player';

const uid = (p: string) => `${p}-${Math.random().toString(36).slice(2, 8)}`;
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'file';

export function adminTabs(on: 'accounts' | 'tutorial') {
  return html`<nav class="subnav"><a href="#/admin" class="${on === 'accounts' ? 'on' : ''}">Accounts</a><a href="#/admin/tutorial" class="${on === 'tutorial' ? 'on' : ''}">Guided tutorial</a></nav>`;
}

export async function tutorialEditView(root: HTMLElement, studio: Studio): Promise<void> {
  const page = shell(root, { studioName: studio.name, active: 'admin' });
  page.innerHTML = '<div class="loading-page">Loading the guide</div>';
  const data: TutorialData = structuredClone(await loadTutorial(true));
  const openSteps = new Set<string>();
  let dirty = false;

  const guard = (e: BeforeUnloadEvent) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
  addEventListener('beforeunload', guard);
  addEventListener('hashchange', () => removeEventListener('beforeunload', guard), { once: true });

  const touch = () => {
    dirty = true;
    const b = $<HTMLButtonElement>('#te-save', page);
    if (b) { b.disabled = false; b.textContent = 'Save and publish'; }
  };

  // every software and software/renderer pair, for the "who is this part for" chips
  const targets = () => data.software.flatMap((s) => [{ id: s.id, label: s.label }, ...s.renderers.map((r) => ({ id: `${s.id}/${r.id}`, label: `${s.label} + ${r.label}` }))]);

  const mediaField = (path: string, m: TutorialMedia | undefined) => html`<div class="te-media" data-media="${path}">
    <div class="te-media-prev">${raw(mediaHtml(m))}</div>
    <div class="te-media-ctl">
      <label class="btn small">${icon('image', 15)}Upload picture or video<input type="file" accept="image/*,video/mp4,video/webm" hidden data-upload="${path}"></label>
      <input class="te-url" data-url="${path}" value="${m?.src ?? ''}" placeholder="…or paste a link (YouTube, Vimeo, image)">
      <input class="te-alt" data-alt="${path}" value="${m?.alt ?? ''}" placeholder="Describe the picture (for screen readers)">
      ${m?.src ? html`<button class="link-btn" data-clear="${path}">${icon('trash', 14)}Remove</button>` : ''}
      <div class="te-progress" hidden><i></i></div>
    </div>
  </div>`;

  const draw = () => {
    const tg = targets();
    page.innerHTML = html`${adminTabs('tutorial')}
      <div class="page-head">
        <div><h1>Guided tutorial</h1><p class="sub">What new studios see when they take the guided tour. Changes go live when you save.</p></div>
        <div class="btn-row">
          <button class="btn" id="te-reset">Reset to built-in</button>
          <button class="btn" id="te-preview">${icon('eye', 16)}Preview</button>
          <button class="btn primary" id="te-save" ${dirty ? '' : raw('disabled')}>${dirty ? 'Save and publish' : 'Saved'}</button>
        </div>
      </div>

      <section class="box te-block">
        <h2>Welcome and finish</h2>
        ${(['welcome', 'finish'] as const).map((k) => html`<div class="te-screen">
          <div class="te-fields">
            <div class="te-tag">${k === 'welcome' ? 'First screen' : 'Last screen'}</div>
            <input class="te-h" data-field="${k}.heading" value="${data[k].heading}">
            <textarea data-field="${k}.body" rows="3">${data[k].body}</textarea>
          </div>
          ${mediaField(`${k}`, data[k].media)}
        </div>`)}
      </section>

      <section class="box te-block">
        <h2>Software and renderers</h2>
        <p class="muted small">Shown on the “What do you use?” screen. Turn one on when its steps are ready.</p>
        <div class="te-software">${data.software.map((s, si) => html`<div class="te-sw">
          <div class="te-sw-row">
            <input data-sw="${si}" data-k="label" value="${s.label}" aria-label="Software name">
            <label class="toggle"><input type="checkbox" data-sw="${si}" data-k="available" ${s.available ? raw('checked') : ''}><span>Available</span></label>
            <button class="icon-btn" data-sw-del="${si}" title="Remove">${icon('trash', 15)}</button>
          </div>
          <div class="te-rs">${s.renderers.map((r, ri) => html`<span class="te-r">
              <input data-r="${si}.${ri}" data-k="label" value="${r.label}" aria-label="Renderer name">
              <label class="toggle"><input type="checkbox" data-r="${si}.${ri}" data-k="available" ${r.available ? raw('checked') : ''}><span>On</span></label>
              <button class="icon-btn" data-r-del="${si}.${ri}" title="Remove">${icon('close', 13)}</button>
            </span>`)}
            <button class="link-btn" data-r-add="${si}">${icon('plus', 13)}Renderer</button>
          </div>
        </div>`)}</div>
        <button class="link-btn" id="te-sw-add">${icon('plus', 14)}Add software</button>
      </section>

      <section class="te-chapters">
        <h2>Parts and steps</h2>
        ${data.chapters.map((c, ci) => html`<article class="box te-ch" data-ch="${ci}">
          <header class="te-ch-head">
            <span class="te-n">${ci + 1}</span>
            <div class="te-ch-fields">
              <input class="te-h" data-ch-f="${ci}" data-k="title" value="${c.title}" placeholder="Part title">
              <input data-ch-f="${ci}" data-k="summary" value="${c.summary}" placeholder="One line about this part">
            </div>
            <div class="te-tools">
              <button class="icon-btn" data-ch-move="${ci}:-1" title="Move up" ${ci === 0 ? raw('disabled') : ''}>${icon('up', 15)}</button>
              <button class="icon-btn" data-ch-move="${ci}:1" title="Move down" ${ci === data.chapters.length - 1 ? raw('disabled') : ''}>${icon('down', 15)}</button>
              <button class="icon-btn" data-ch-del="${ci}" title="Delete part">${icon('trash', 15)}</button>
            </div>
          </header>
          <div class="te-for"><span>Shown for</span>
            <label class="pill-check"><input type="checkbox" data-for-all="${ci}" ${c.for.length ? '' : raw('checked')}><span>Everyone</span></label>
            ${tg.map((t) => html`<label class="pill-check"><input type="checkbox" data-for="${ci}" value="${t.id}" ${c.for.includes(t.id) ? raw('checked') : ''}><span>${t.label}</span></label>`)}
          </div>
          <div class="te-steps">${c.steps.map((s, si) => {
            const isOpen = openSteps.has(s.id);
            return html`<div class="te-step ${isOpen ? 'open' : ''} ${s.hidden ? 'hidden-step' : ''}" data-step="${ci}.${si}">
              <button class="te-step-head" data-toggle="${s.id}">
                <span class="te-thumb">${raw(mediaHtml(s.media))}</span>
                <span class="te-step-title"><strong>${s.heading || 'Untitled step'}</strong><em>${s.hidden ? 'Hidden' : s.body.replace(/\*\*|^- /gm, '').replace(/\s+/g, ' ').slice(0, 110)}</em></span>
                ${icon(isOpen ? 'up' : 'down', 16)}
              </button>
              ${isOpen ? html`<div class="te-step-body">
                <div class="te-fields">
                  <input class="te-h" data-st="${ci}.${si}" data-k="heading" value="${s.heading}" placeholder="Heading">
                  <textarea data-st="${ci}.${si}" data-k="body" rows="6" placeholder="A few short sentences">${s.body}</textarea>
                  <p class="muted tiny">Blank line = new paragraph · start lines with “- ” for bullets · **bold**</p>
                  <div class="te-dl">
                    <input data-dl="${ci}.${si}" data-k="label" value="${s.download?.label ?? ''}" placeholder="Download button label (optional)">
                    <input data-dl="${ci}.${si}" data-k="url" value="${s.download?.url ?? ''}" placeholder="Download link">
                  </div>
                  <div class="te-tools">
                    <label class="toggle"><input type="checkbox" data-hide="${ci}.${si}" ${s.hidden ? raw('checked') : ''}><span>Hide this step</span></label>
                    <span class="spacer"></span>
                    <button class="icon-btn" data-st-move="${ci}.${si}:-1" title="Move up" ${si === 0 ? raw('disabled') : ''}>${icon('up', 15)}</button>
                    <button class="icon-btn" data-st-move="${ci}.${si}:1" title="Move down" ${si === c.steps.length - 1 ? raw('disabled') : ''}>${icon('down', 15)}</button>
                    <button class="icon-btn" data-st-del="${ci}.${si}" title="Delete step">${icon('trash', 15)}</button>
                  </div>
                </div>
                ${mediaField(`chapters.${ci}.steps.${si}`, s.media)}
              </div>` : ''}
            </div>`;
          })}</div>
          <button class="link-btn" data-st-add="${ci}">${icon('plus', 14)}Add step</button>
        </article>`)}
        <button class="btn" id="te-ch-add">${icon('plus', 15)}Add part</button>
      </section>`.html;
    wire();
  };

  // media lives at data.welcome.media, data.finish.media or data.chapters[i].steps[j].media
  const holder = (path: string): { media?: TutorialMedia } => {
    if (path === 'welcome' || path === 'finish') return data[path];
    const [, ci, , si] = path.split('.');
    return data.chapters[+ci].steps[+si];
  };
  const pair = (v: string) => v.split('.').map(Number) as [number, number];

  function wire(): void {
    $('#te-preview', page).onclick = () => { void openTutorial({ data: structuredClone(data) }); };
    $('#te-save', page).onclick = async () => {
      const b = $<HTMLButtonElement>('#te-save', page);
      b.disabled = true; b.textContent = 'Saving…';
      try {
        await adminPutSite('tutorial.json', new Blob([JSON.stringify(data, null, 1)], { type: 'application/json' }));
        forgetTutorial();
        dirty = false; b.textContent = 'Saved';
        toast('Guide saved. New studios see it now.');
      } catch (e) { b.disabled = false; b.textContent = 'Save and publish'; toast(e instanceof Error ? e.message : 'Couldn’t save', 'error', 8000); }
    };
    $('#te-reset', page).onclick = async () => {
      if (!(await confirmDialog({ title: 'Reset to the built-in guide?', body: 'Your edits are replaced with the original steps. Nothing changes for studios until you save.', confirm: 'Reset', danger: true }))) return;
      Object.assign(data, structuredClone(DEFAULT_TUTORIAL)); touch(); draw();
    };

    // plain text fields
    $$<HTMLInputElement | HTMLTextAreaElement>('[data-field]', page).forEach((el) => (el.oninput = () => {
      const [k, f] = el.dataset.field!.split('.') as ['welcome' | 'finish', 'heading' | 'body'];
      data[k][f] = el.value; touch();
    }));
    $$<HTMLInputElement>('[data-ch-f]', page).forEach((el) => (el.oninput = () => {
      (data.chapters[+el.dataset.chF!] as unknown as Record<string, string>)[el.dataset.k!] = el.value; touch();
    }));
    $$<HTMLInputElement | HTMLTextAreaElement>('[data-st]', page).forEach((el) => (el.oninput = () => {
      const [ci, si] = pair(el.dataset.st!);
      (data.chapters[ci].steps[si] as unknown as Record<string, string>)[el.dataset.k!] = el.value; touch();
      if (el.dataset.k === 'heading') el.closest('.te-step')!.querySelector('.te-step-title strong')!.textContent = el.value || 'Untitled step';
    }));
    $$<HTMLInputElement>('[data-dl]', page).forEach((el) => (el.oninput = () => {
      const [ci, si] = pair(el.dataset.dl!);
      const s = data.chapters[ci].steps[si];
      const d = { label: '', url: '', ...(s.download ?? {}) };
      (d as Record<string, string>)[el.dataset.k!] = el.value.trim();
      s.download = d.label && d.url ? d : d.label || d.url ? d : undefined;
      touch();
    }));
    $$<HTMLInputElement>('[data-hide]', page).forEach((el) => (el.onchange = () => {
      const [ci, si] = pair(el.dataset.hide!); data.chapters[ci].steps[si].hidden = el.checked || undefined; touch(); draw();
    }));

    // software and renderers
    $$<HTMLInputElement>('[data-sw]', page).forEach((el) => (el.onchange = el.oninput = () => {
      const s = data.software[+el.dataset.sw!];
      if (el.dataset.k === 'available') s.available = el.checked; else { s.label = el.value; if (!s.id) s.id = slug(el.value); }
      touch();
    }));
    $$<HTMLInputElement>('[data-r]', page).forEach((el) => (el.onchange = el.oninput = () => {
      const [si, ri] = pair(el.dataset.r!); const r = data.software[si].renderers[ri];
      if (el.dataset.k === 'available') r.available = el.checked; else { r.label = el.value; if (!r.id) r.id = slug(el.value); }
      touch();
    }));
    $$<HTMLButtonElement>('[data-sw-del]', page).forEach((b) => (b.onclick = () => { data.software.splice(+b.dataset.swDel!, 1); touch(); draw(); }));
    $$<HTMLButtonElement>('[data-r-del]', page).forEach((b) => (b.onclick = () => { const [si, ri] = pair(b.dataset.rDel!); data.software[si].renderers.splice(ri, 1); touch(); draw(); }));
    $$<HTMLButtonElement>('[data-r-add]', page).forEach((b) => (b.onclick = () => {
      const name = prompt('Renderer name, e.g. V-Ray'); if (!name?.trim()) return;
      data.software[+b.dataset.rAdd!].renderers.push({ id: slug(name), label: name.trim(), available: false }); touch(); draw();
    }));
    $('#te-sw-add', page).onclick = () => {
      const name = prompt('Software name, e.g. Blender'); if (!name?.trim()) return;
      data.software.push({ id: slug(name), label: name.trim(), available: false, renderers: [] }); touch(); draw();
    };

    // who each part is for
    $$<HTMLInputElement>('[data-for-all]', page).forEach((el) => (el.onchange = () => { data.chapters[+el.dataset.forAll!].for = []; touch(); draw(); }));
    $$<HTMLInputElement>('[data-for]', page).forEach((el) => (el.onchange = () => {
      const c = data.chapters[+el.dataset.for!];
      c.for = el.checked ? [...new Set([...c.for, el.value])] : c.for.filter((x) => x !== el.value);
      touch(); draw();
    }));

    // parts and steps: add, move, delete, open
    const move = <T>(list: T[], i: number, d: number) => { const j = i + d; if (j < 0 || j >= list.length) return; [list[i], list[j]] = [list[j], list[i]]; };
    $$<HTMLButtonElement>('[data-ch-move]', page).forEach((b) => (b.onclick = () => { const [i, d] = b.dataset.chMove!.split(':').map(Number); move(data.chapters, i, d); touch(); draw(); }));
    $$<HTMLButtonElement>('[data-ch-del]', page).forEach((b) => (b.onclick = async () => {
      const c = data.chapters[+b.dataset.chDel!];
      if (!(await confirmDialog({ title: `Delete “${c.title}”?`, body: `Its ${c.steps.length} step${c.steps.length === 1 ? '' : 's'} go too.`, confirm: 'Delete part', danger: true }))) return;
      data.chapters.splice(+b.dataset.chDel!, 1); touch(); draw();
    }));
    $('#te-ch-add', page).onclick = () => {
      const c: TutorialChapter = { id: uid('part'), title: 'New part', summary: '', for: [], steps: [{ id: uid('step'), heading: 'New step', body: '' }] };
      data.chapters.push(c); openSteps.add(c.steps[0].id); touch(); draw();
    };
    $$<HTMLButtonElement>('[data-st-move]', page).forEach((b) => (b.onclick = () => {
      const [pos, d] = b.dataset.stMove!.split(':'); const [ci, si] = pair(pos); move(data.chapters[ci].steps, si, +d); touch(); draw();
    }));
    $$<HTMLButtonElement>('[data-st-del]', page).forEach((b) => (b.onclick = async () => {
      const [ci, si] = pair(b.dataset.stDel!);
      if (!(await confirmDialog({ title: 'Delete this step?', body: data.chapters[ci].steps[si].heading, confirm: 'Delete step', danger: true }))) return;
      data.chapters[ci].steps.splice(si, 1); touch(); draw();
    }));
    $$<HTMLButtonElement>('[data-st-add]', page).forEach((b) => (b.onclick = () => {
      const s = { id: uid('step'), heading: 'New step', body: '' };
      data.chapters[+b.dataset.stAdd!].steps.push(s); openSteps.add(s.id); touch(); draw();
    }));
    $$<HTMLButtonElement>('[data-toggle]', page).forEach((b) => (b.onclick = () => {
      const id = b.dataset.toggle!; if (openSteps.has(id)) openSteps.delete(id); else openSteps.add(id); draw();
    }));

    // pictures and videos
    $$<HTMLInputElement>('[data-url]', page).forEach((el) => (el.onchange = () => {
      const h = holder(el.dataset.url!); const v = el.value.trim();
      h.media = v ? { src: v, alt: h.media?.alt } : undefined; touch(); draw();
    }));
    $$<HTMLInputElement>('[data-alt]', page).forEach((el) => (el.oninput = () => {
      const h = holder(el.dataset.alt!); if (h.media) { h.media.alt = el.value; touch(); }
    }));
    $$<HTMLButtonElement>('[data-clear]', page).forEach((b) => (b.onclick = () => { holder(b.dataset.clear!).media = undefined; touch(); draw(); }));
    $$<HTMLInputElement>('[data-upload]', page).forEach((el) => (el.onchange = async () => {
      const file = el.files?.[0]; if (!file) return;
      const ext = (file.name.split('.').pop() ?? '').toLowerCase().replace('jpeg', 'jpg');
      if (!['jpg', 'png', 'webp', 'gif', 'svg', 'mp4', 'webm'].includes(ext)) { toast('Use a JPG, PNG, WebP, GIF, SVG, MP4 or WebM file', 'error'); return; }
      if (file.size > 80 * 1024 * 1024) { toast('That file is over 80 MB. Shorten the video or use a YouTube link.', 'error', 7000); return; }
      const box = el.closest<HTMLElement>('.te-media')!; const bar = box.querySelector<HTMLElement>('.te-progress')!;
      bar.hidden = false;
      try {
        const name = `tutorial/${slug(file.name.replace(/\.[^.]+$/, ''))}-${Date.now().toString(36)}.${ext}`;
        const url = await adminPutSite(name, file, (f) => { bar.querySelector('i')!.setAttribute('style', `width:${(f * 100).toFixed(0)}%`); });
        const h = holder(el.dataset.upload!);
        h.media = { src: url, alt: h.media?.alt ?? '' };
        touch(); draw(); toast('Uploaded. Save to publish it.');
      } catch (e) { bar.hidden = true; toast(e instanceof Error ? e.message : 'Upload failed', 'error', 8000); }
    }));
  }

  draw();
}
